import React, { useState, useEffect, useMemo, useRef } from "react";
import { useParams } from "react-router-dom";
import api from "../../../const/axios";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Helmet } from "react-helmet";
import { cn } from "@/lib/utils";
import {
  Plus, Trash2, CheckCircle2, XCircle, Eye,
  Cloud, CloudOff, Loader2, Lock, Unlock, FileSpreadsheet, Flag, Upload,
} from "lucide-react";
import { exportToExcel } from "./exportToExcel";
import { importFromExcel } from "./importFromExcel";

// ─── Types ────────────────────────────────────────────────────────────────────

interface GameFormatRound {
  order: number;
  questionCount: number;
}

interface GameFormat {
  id: number;
  name: string;
  rounds: GameFormatRound[];
}

interface GameData {
  id: number;
  slug: string;
  name: string;
  date: string;
  isApproved: boolean | null;
  isLocked: boolean;
  isCompleted: boolean;
  canEdit: boolean;
  results: string | null;
  format: GameFormat;
}

interface Team {
  key: number;
  name: string;
  teamId: number | null;
  answers: (boolean | null)[][];
}

interface SystemTeam {
  id: number;
  name: string;
}

type SaveStatus = "idle" | "saving" | "saved" | "error";

const normalizeTeamName = (name: string): string =>
  name
    .toLocaleLowerCase("az")
    .replace(/[ə]/g, "e")
    .replace(/[ğ]/g, "g")
    .replace(/[ı]/g, "i")
    .replace(/[ö]/g, "o")
    .replace(/[ü]/g, "u")
    .replace(/[ş]/g, "s")
    .replace(/[ç]/g, "c")
    .replace(/[^a-z0-9\s]/g, " ")
    .replace(/\s+/g, " ")
    .trim();

// ─── Answer cell ──────────────────────────────────────────────────────────────

const AnswerCell: React.FC<{
  value: boolean | null;
  canEdit: boolean;
  onClick: () => void;
}> = ({ value, canEdit, onClick }) => (
  <button
    type="button"
    onClick={canEdit ? onClick : undefined}
    className={cn(
      "w-8 h-8 flex items-center justify-center mx-auto rounded-full transition-transform duration-100",
      canEdit && "hover:scale-110",
      !canEdit && "cursor-default pointer-events-none"
    )}
  >
    {value === true  && <CheckCircle2 className="w-6 h-6 text-green-500" />}
    {value === false && <XCircle      className="w-6 h-6 text-red-400"   />}
    {value === null  && <div className="w-5 h-5 rounded-full border-2 border-dashed border-gray-200 dark:border-gray-700" />}
  </button>
);

// ─── Save indicator ───────────────────────────────────────────────────────────

const SaveIndicator: React.FC<{ status: SaveStatus }> = ({ status }) => {
  if (status === "idle") return null;
  return (
    <div className={cn(
      "flex items-center gap-1.5 text-xs font-medium transition-all",
      status === "saving" && "text-gray-400 dark:text-gray-500",
      status === "saved"  && "text-green-600",
      status === "error"  && "text-red-500",
    )}>
      {status === "saving" && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
      {status === "saved"  && <Cloud    className="w-3.5 h-3.5" />}
      {status === "error"  && <CloudOff className="w-3.5 h-3.5" />}
      {status === "saving" && "Saxlanılır..."}
      {status === "saved"  && "Saxlanıldı"}
      {status === "error"  && "Xəta"}
    </div>
  );
};

// ─── Main component ───────────────────────────────────────────────────────────

const LiveGame: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();

  const [game, setGame]           = useState<GameData | null>(null);
  const [teams, setTeams]         = useState<Team[]>([]);
  const [activeRound, setActiveRound] = useState(0);
  const [newTeamName, setNewTeamName] = useState("");
  const [loading, setLoading]     = useState(true);
  const [allTeams, setAllTeams]   = useState<SystemTeam[]>([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [isLocked, setIsLocked]   = useState(false);
  const [canEdit, setCanEdit]     = useState(false);
  const [saveStatus, setSaveStatus] = useState<SaveStatus>("idle");
  const [lockLoading, setLockLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [isCompleted, setIsCompleted] = useState(false);
  const [completeLoading, setCompleteLoading] = useState(false);
  const [mappingOpen, setMappingOpen] = useState(false);
  const [mappings, setMappings] = useState<Record<number, number | null>>({});
  const [mappingSearch, setMappingSearch] = useState<Record<number, string>>({});
  const [mappingFocusedKey, setMappingFocusedKey] = useState<number | null>(null);
  const [showOnlyUnmapped, setShowOnlyUnmapped] = useState(false);
  const [importingExcel, setImportingExcel] = useState(false);

  const isFirstSave = useRef(true);
  const excelInputRef = useRef<HTMLInputElement | null>(null);

  // ── Fetch all teams for autocomplete ───────────────────────────────────────

  useEffect(() => {
    api.get("teams")
      .then(res => {
        const list = Array.isArray(res.data)
          ? res.data
            .filter((t: any) => Number.isFinite(t?.id) && typeof t?.name === "string")
            .map((t: any) => ({ id: Number(t.id), name: t.name as string }))
          : [];
        setAllTeams(list);
      })
      .catch(() => {});
  }, []);

  // ── Load game data ─────────────────────────────────────────────────────────

  useEffect(() => {
    if (!slug) return;
    setLoading(true);
    api.get(`organizer-games/${slug}`)
      .then(res => {
        const g: GameData = res.data;
        setGame(g);
        setIsLocked(g.isLocked);
        setIsCompleted(g.isCompleted);
        setCanEdit(g.canEdit);

        if (g.results) {
          try {
            const parsed = JSON.parse(g.results);
            if (parsed.teams) setTeams(parsed.teams);
          } catch {
            // ignore
          }
        }
      })
      .catch(err => {
        console.error("Failed to load game", err);
      })
      .finally(() => setLoading(false));
  }, [slug]);

  // ── Debounced auto-save ────────────────────────────────────────────────────

  useEffect(() => {
    if (!slug || !canEdit) return;
    if (isFirstSave.current) { isFirstSave.current = false; return; }

    setSaveStatus("saving");
    const handler = setTimeout(async () => {
      try {
        await api.put(`organizer-games/${slug}/results`, JSON.stringify({
          results: JSON.stringify({ teams, updatedAt: new Date().toISOString() }),
        }));
        setSaveStatus("saved");
        setTimeout(() => setSaveStatus("idle"), 2500);
      } catch {
        setSaveStatus("error");
      }
    }, 3000);

    return () => clearTimeout(handler);
  }, [teams, slug, canEdit]);

  // ── Handlers ───────────────────────────────────────────────────────────────

  const makeEmptyAnswers = (format: GameFormat): (boolean | null)[][] =>
    format.rounds.map((r) => Array(r.questionCount).fill(null));

  const addTeam = () => {
    if (!canEdit || !newTeamName.trim() || !game) return;
    const normalizedInput = normalizeTeamName(newTeamName);
    const existingTeam = allTeams.find(t => normalizeTeamName(t.name) === normalizedInput);
    setTeams(prev => [...prev, {
      key: Date.now(),
      name: newTeamName.trim(),
      teamId: existingTeam?.id ?? null,
      answers: makeEmptyAnswers(game.format),
    }]);
    setNewTeamName("");
    setShowDropdown(false);
  };

  const toggleAnswer = (teamKey: number, roundIdx: number, qIdx: number) => {
    if (!canEdit) return;
    setTeams(prev => prev.map(team => {
      if (team.key !== teamKey) return team;
      const answers = team.answers.map(r => [...r]);
      const cur = answers[roundIdx][qIdx];
      if (cur === true)       answers[roundIdx][qIdx] = false;
      else if (cur === false) answers[roundIdx][qIdx] = null;
      else                    answers[roundIdx][qIdx] = true;
      return { ...team, answers };
    }));
  };

  const deleteTeam = (key: number) => {
    if (!canEdit || isLocked) return;
    setTeams(prev => prev.filter(t => t.key !== key));
  };

  const handleExport = async () => {
    if (!game || exporting) return;
    setExporting(true);
    try {
      await exportToExcel(game, sortedTeams);
    } finally {
      setExporting(false);
    }
  };

  const triggerExcelImport = () => {
    if (!canEdit || importingExcel) return;
    excelInputRef.current?.click();
  };

  const handleExcelImport = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file || !game || importingExcel || !canEdit) return;

    setImportingExcel(true);
    try {
      const result = await importFromExcel(file, game, teams, allTeams);
      setTeams(result.teams);
      if (result.warnings.length > 0) {
        window.alert(`Excel idxalı tamamlandı.\n\nQeydlər:\n- ${result.warnings.join("\n- ")}`);
      }
    } catch (err: any) {
      const message = err?.message || "Excel idxalı mümkün olmadı.";
      window.alert(message);
    } finally {
      setImportingExcel(false);
    }
  };

  const toggleLock = async () => {
    if (!slug) return;
    setLockLoading(true);
    try {
      const res = await api.put(`organizer-games/${slug}/lock`);
      setIsLocked(res.data.isLocked);
    } catch {
      // ignore
    } finally {
      setLockLoading(false);
    }
  };

  const exactTeamIndex = useMemo(() => {
    const index = new Map<string, SystemTeam[]>();
    for (const st of allTeams) {
      const key = normalizeTeamName(st.name);
      if (!key) continue;
      if (!index.has(key)) index.set(key, []);
      index.get(key)!.push(st);
    }
    return index;
  }, [allTeams]);

  const getExactMatches = (teamName: string): SystemTeam[] =>
    exactTeamIndex.get(normalizeTeamName(teamName)) ?? [];

  const runAutoMapping = (
    currentMappings: Record<number, number | null>,
    currentSearch: Record<number, string>
  ) => {
    const nextMappings = { ...currentMappings };
    const nextSearch = { ...currentSearch };
    let applied = 0;

    for (const team of teams) {
      if (nextMappings[team.key] != null) continue;
      const exactMatches = getExactMatches(team.name);
      if (exactMatches.length === 1) {
        nextMappings[team.key] = exactMatches[0].id;
        nextSearch[team.key] = exactMatches[0].name;
        applied++;
      }
    }

    return { nextMappings, nextSearch, applied };
  };

  const openMappingModal = () => {
    // Pre-fill mappings from existing teamId values
    const initial: Record<number, number | null> = {};
    const initialSearch: Record<number, string> = {};
    teams.forEach(t => {
      initial[t.key] = t.teamId;
      const matched = allTeams.find(dt => dt.id === t.teamId);
      initialSearch[t.key] = matched ? matched.name : "";
    });
    const { nextMappings, nextSearch } = runAutoMapping(initial, initialSearch);
    setMappings(nextMappings);
    setMappingSearch(nextSearch);
    setMappingFocusedKey(null);
    setShowOnlyUnmapped(false);
    setMappingOpen(true);
  };

  const autoMapNow = () => {
    const { nextMappings, nextSearch, applied } = runAutoMapping(mappings, mappingSearch);
    setMappings(nextMappings);
    setMappingSearch(nextSearch);
    if (applied > 0) {
      window.alert(`${applied} komanda avtomatik uyğunlaşdırıldı.`);
    } else {
      window.alert("Avtomatik uyğunlaşdırma üçün yeni dəqiq uyğun tapılmadı.");
    }
  };

  const confirmAndComplete = async () => {
    if (!slug || completeLoading) return;
    setCompleteLoading(true);
    try {
      const { nextMappings, nextSearch } = runAutoMapping(mappings, mappingSearch);
      setMappings(nextMappings);
      setMappingSearch(nextSearch);

      const mismatches = teams
        .map(team => {
          const exactMatches = getExactMatches(team.name);
          const mappedId = nextMappings[team.key];
          if (exactMatches.length !== 1 || mappedId == null) return null;
          return mappedId === exactMatches[0].id
            ? null
            : { name: team.name, expected: exactMatches[0].name };
        })
        .filter(Boolean) as { name: string; expected: string }[];

      if (mismatches.length > 0) {
        const lines = mismatches.slice(0, 5).map(m => `- ${m.name} → ${m.expected}`).join("\n");
        window.alert(`Bəzi komandalar səhv uyğunlaşdırılıb. Düzəldin:\n${lines}`);
        return;
      }

      // Apply mappings to teams array
      const updatedTeams = teams.map(t => ({ ...t, teamId: nextMappings[t.key] ?? null }));
      // Force-save results with updated teamIds
      await api.put(`organizer-games/${slug}/results`, JSON.stringify({
        results: JSON.stringify({ teams: updatedTeams, updatedAt: new Date().toISOString() }),
      }));
      setTeams(updatedTeams);
      // Mark complete
      await api.put(`organizer-games/${slug}/complete`);
      setIsCompleted(true);
      setCanEdit(false);
      setMappingOpen(false);
    } catch {
      // ignore
    } finally {
      setCompleteLoading(false);
    }
  };

  // ── Derived values ─────────────────────────────────────────────────────────

  const rounds = game?.format.rounds ?? [];
  const summaryTabIndex = rounds.length;
  const isSummaryTab = activeRound === summaryTabIndex;
  const qCount = !isSummaryTab ? (game?.format.rounds[activeRound]?.questionCount ?? 0) : 0;

  // ratings for the active round only (for difficulty row)
  const questionRatings = isSummaryTab ? [] : Array.from({ length: qCount }, (_, qi) => {
    const correct = teams.filter(t => t.answers[activeRound]?.[qi] === true).length;
    return teams.length > 0 ? teams.length - correct : 0;
  });

  const getTeamScore = (team: Team) => {
    if (!game) return 0;
    let score = 0;
    game.format.rounds.forEach((round, r) => {
      const roundRatings = Array.from({ length: round.questionCount }, (_, qi) => {
        const correct = teams.filter(t => t.answers[r]?.[qi] === true).length;
        return teams.length > 0 ? teams.length - correct : 0;
      });
      team.answers[r]?.forEach((ans, qi) => {
        if (ans === true) score += roundRatings[qi];
      });
    });
    return score;
  };

  const getTeamCorrectInRound = (team: Team, roundIdx: number) =>
    (team.answers[roundIdx] ?? []).filter(a => a === true).length;

  const filteredTeams = allTeams.filter(t =>
    newTeamName && t.name.toUpperCase().includes(newTeamName.toUpperCase())
  );

  const sortedTeams = [...teams].sort((a, b) => getTeamScore(b) - getTeamScore(a));

  const mappingMeta = useMemo(() => {
    const byTeam: Record<number, {
      exactMatches: SystemTeam[];
      mappedId: number | null;
      isMapped: boolean;
      isExact: boolean;
      isMismatch: boolean;
      canAutoMap: boolean;
    }> = {};

    let mappedCount = 0;
    let autoMappableCount = 0;
    let mismatchCount = 0;

    for (const team of sortedTeams) {
      const exactMatches = exactTeamIndex.get(normalizeTeamName(team.name)) ?? [];
      const mappedId = mappings[team.key] ?? null;
      const isMapped = mappedId != null;
      const isExact = exactMatches.length === 1 && mappedId === exactMatches[0].id;
      const isMismatch = exactMatches.length === 1 && isMapped && mappedId !== exactMatches[0].id;
      const canAutoMap = exactMatches.length === 1 && !isMapped;

      if (isMapped) mappedCount++;
      if (canAutoMap) autoMappableCount++;
      if (isMismatch) mismatchCount++;

      byTeam[team.key] = {
        exactMatches,
        mappedId,
        isMapped,
        isExact,
        isMismatch,
        canAutoMap,
      };
    }

    return {
      byTeam,
      mappedCount,
      unmappedCount: sortedTeams.length - mappedCount,
      autoMappableCount,
      mismatchCount,
    };
  }, [sortedTeams, mappings, exactTeamIndex]);

  const visibleMappingTeams = showOnlyUnmapped
    ? sortedTeams.filter(team => (mappings[team.key] ?? null) == null)
    : sortedTeams;

  // ── Cell classes ──────────────────────────────────────────────────────────
  const thCls = "px-3 py-3 text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wide bg-gray-50/80 border-b border-gray-100 dark:border-gray-800 whitespace-nowrap";
  const tdCls = "px-3 py-2.5 border-b border-gray-50 text-sm";

  const W_DEL = 48;
  const W_RAT = 80;
  const W_DC  = 64;

  // ── Loading / pending states ───────────────────────────────────────────────

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-950 pt-16 flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-blue-500 animate-spin dark:text-indigo-300" />
      </div>
    );
  }

  if (!game) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-950 pt-16 flex items-center justify-center">
        <p className="text-gray-400 dark:text-gray-500 font-medium">Oyun tapılmadı.</p>
      </div>
    );
  }

  if (game.isApproved !== true) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-950 pt-16 flex items-center justify-center px-4">
        <div className="text-center space-y-3">
          <p className="text-5xl font-black text-gray-200">⏳</p>
          <p className="text-lg font-semibold text-gray-500 dark:text-gray-400">Oyun hələ təsdiqlənməyib</p>
          <p className="text-sm text-gray-400 dark:text-gray-500">Administrator oyununuzu təsdiqləyəndən sonra masa açılacaq.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-950 pt-16">
      <Helmet>
        <title>{game.name} | 3sual.az</title>
      </Helmet>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 space-y-4">

        {/* ── Header card ───────────────────────────────────────────────── */}
        <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-100 dark:border-gray-800 shadow-sm px-6 py-5">
          <div className="flex flex-col sm:flex-row sm:items-center gap-4">
            <div className="flex items-center gap-3 flex-1 min-w-0">
              <h1 className="text-xl font-black text-gray-900 dark:text-gray-50 truncate">{game.name}</h1>
              {isCompleted && (
                <span className="flex items-center gap-1 text-xs font-semibold text-purple-700 bg-purple-50 border border-purple-200 px-2 py-0.5 rounded-full flex-shrink-0">
                  <Flag className="w-3 h-3" />
                  Tamamlandı
                </span>
              )}
              {!canEdit && !isCompleted && (
                <span className="flex items-center gap-1 text-xs font-semibold text-amber-600 bg-amber-50 border border-amber-200 px-2 py-0.5 rounded-full flex-shrink-0">
                  <Eye className="w-3 h-3" />
                  Yalnız izləmək
                </span>
              )}
              <SaveIndicator status={saveStatus} />
            </div>

            {/* Toolbar */}
            <div className="flex items-center gap-2 flex-wrap">
              {canEdit && (
                <>
                  <div className="relative">
                    <Input
                      placeholder="Komanda adı"
                      value={newTeamName}
                      disabled={isLocked}
                      onChange={e => { setNewTeamName(e.target.value); setShowDropdown(true); }}
                      onKeyDown={e => e.key === "Enter" && addTeam()}
                      onBlur={() => setTimeout(() => setShowDropdown(false), 150)}
                      className="w-48"
                    />
                    {showDropdown && filteredTeams.length > 0 && (
                      <div className="absolute z-50 w-full mt-1 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl shadow-lg overflow-hidden max-h-48 overflow-y-auto">
                        {filteredTeams.map((t: any) => (
                          <button
                            key={t.id}
                            type="button"
                            className="w-full px-3 py-2 text-left text-sm hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
                            onClick={() => { setNewTeamName(t.name); setShowDropdown(false); }}
                          >
                            {t.name}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                  <Button
                    onClick={addTeam}
                    disabled={!newTeamName.trim() || isLocked}
                    className="gap-1.5"
                    size="sm"
                  >
                    <Plus className="w-3.5 h-3.5" />
                    Komanda əlavə et
                  </Button>
                  <Button
                    onClick={toggleLock}
                    disabled={lockLoading}
                    variant="outline"
                    size="sm"
                    className="gap-1.5"
                  >
                    {isLocked ? <Unlock className="w-3.5 h-3.5" /> : <Lock className="w-3.5 h-3.5" />}
                    {isLocked ? "Kilidi aç" : "Kilid"}
                  </Button>
                  <Button
                    onClick={openMappingModal}
                    disabled={completeLoading || teams.length === 0}
                    variant="outline"
                    size="sm"
                    className="gap-1.5 border-purple-200 text-purple-700 hover:bg-purple-50"
                  >
                    <Flag className="w-3.5 h-3.5" />
                    Tamamla
                  </Button>
                </>
              )}
              <Button
                onClick={handleExport}
                disabled={exporting || teams.length === 0}
                variant="outline"
                size="sm"
                className="gap-1.5"
              >
                {exporting
                  ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  : <FileSpreadsheet className="w-3.5 h-3.5" />}
                Excelə çıxar
              </Button>
              {canEdit && (
                <>
                  <Button
                    onClick={triggerExcelImport}
                    disabled={importingExcel}
                    variant="outline"
                    size="sm"
                    className="gap-1.5"
                  >
                    {importingExcel
                      ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                      : <Upload className="w-3.5 h-3.5" />}
                    Exceldən idxal et
                  </Button>
                  <input
                    ref={excelInputRef}
                    type="file"
                    className="hidden"
                    accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                    onChange={handleExcelImport}
                  />
                </>
              )}
            </div>
          </div>

          {/* Format label */}
          <p className="text-xs text-gray-400 dark:text-gray-500 font-medium mt-2">{game.format.name}</p>
        </div>

        {/* ── Round tabs ────────────────────────────────────────────────── */}
        <div className="flex gap-2 overflow-x-auto pb-1">
          {rounds.map((round, r) => (
            <button
              key={r}
              type="button"
              onClick={() => setActiveRound(r)}
              className={cn(
                "flex-shrink-0 px-4 py-2 rounded-xl text-sm font-semibold transition-all",
                activeRound === r
                  ? "bg-blue-500 text-white shadow-sm"
                  : "bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 hover:border-blue-200 dark:hover:border-blue-900 hover:text-blue-600 dark:hover:text-indigo-200"
              )}
            >
              {r + 1}-ci tur
            </button>
          ))}
          <button
            type="button"
            onClick={() => setActiveRound(summaryTabIndex)}
            className={cn(
              "flex-shrink-0 px-4 py-2 rounded-xl text-sm font-semibold transition-all",
              isSummaryTab
                ? "bg-blue-500 text-white shadow-sm"
                : "bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 hover:border-blue-200 dark:hover:border-blue-900 hover:text-blue-600 dark:hover:text-indigo-200"
            )}
          >
            Yekun
          </button>
        </div>

        {/* ── Score table ───────────────────────────────────────────────── */}
        <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-100 dark:border-gray-800 shadow-sm overflow-hidden">
          {teams.length === 0 ? (
            <div className="py-20 text-center">
              <p className="text-gray-400 dark:text-gray-500 font-medium">
                {canEdit && !isLocked ? "Komanda əlavə edin" : "Hələ ki, heç bir komanda yoxdur"}
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full border-collapse text-sm">
                <thead>
                  <tr>
                    <th className={cn(thCls, "sticky left-0 z-20 text-left min-w-[180px]")}>
                      Komanda adı
                    </th>
                    {isSummaryTab
                      ? rounds.map((_, i) => (
                        <th key={`summary-r-${i}`} className={cn(thCls, "text-center w-[92px]")}>
                          Tur {i + 1}
                        </th>
                      ))
                      : Array.from({ length: qCount }, (_, i) => (
                        <th key={i} className={cn(thCls, "text-center w-[72px]")}>
                          {i + 1}
                        </th>
                      ))}
                    <th
                      className={cn(thCls, "text-center z-20 border-l border-gray-100 dark:border-gray-800")}
                      style={{ position: "sticky", right: W_DEL + W_RAT, width: W_DC }}
                    >
                      {isSummaryTab ? "DC (Cəmi)" : "DC"}
                    </th>
                    <th
                      className={cn(thCls, "text-center z-20")}
                      style={{ position: "sticky", right: W_DEL, width: W_RAT }}
                    >
                      REYTİNQ
                    </th>
                    <th
                      className={cn(thCls, "z-20")}
                      style={{ position: "sticky", right: 0, width: W_DEL }}
                    />
                  </tr>
                </thead>

                <tbody>
                  {sortedTeams.map((team, rowIdx) => {
                    const correct = isSummaryTab
                      ? rounds.reduce((acc, _, r) => acc + getTeamCorrectInRound(team, r), 0)
                      : getTeamCorrectInRound(team, activeRound);
                    const score   = getTeamScore(team);
                    const isTop   = rowIdx === 0 && score > 0;

                    return (
                      <tr
                        key={team.key}
                        className={cn(
                          "transition-colors hover:bg-gray-50/60",
                          isTop && "bg-amber-50/40"
                        )}
                      >
                        <td className={cn(
                          tdCls,
                          "sticky left-0 z-10 bg-inherit font-semibold text-gray-800 dark:text-gray-100 min-w-[180px]",
                          isTop && "bg-amber-50/40"
                        )}>
                          <div className="flex items-center gap-2">
                            {isTop && <span className="text-amber-500 text-xs font-bold">🥇</span>}
                            {!isTop && <span className="text-xs text-gray-300 font-medium w-4 text-center">{rowIdx + 1}</span>}
                            {team.name}
                          </div>
                        </td>

                        {isSummaryTab
                          ? rounds.map((_, r) => {
                            const roundDC = getTeamCorrectInRound(team, r);
                            return (
                              <td key={`rdc-${r}`} className={cn(tdCls, "text-center w-[92px]")}>
                                <span className="inline-flex items-center justify-center min-w-[1.75rem] px-1.5 py-0.5 rounded-full text-xs font-semibold bg-indigo-50 text-indigo-700">
                                  {roundDC}
                                </span>
                              </td>
                            );
                          })
                          : (team.answers[activeRound] ?? Array(qCount).fill(null)).map((ans, qi) => (
                            <td key={qi} className={cn(tdCls, "text-center w-[72px]")}>
                              <AnswerCell
                                value={ans}
                                canEdit={canEdit}
                                onClick={() => toggleAnswer(team.key, activeRound, qi)}
                              />
                            </td>
                          ))}

                        <td
                          className={cn(tdCls, "text-center font-bold z-10 border-l border-gray-100 dark:border-gray-800", isTop ? "bg-amber-50/40" : "bg-white dark:bg-gray-900")}
                          style={{ position: "sticky", right: W_DEL + W_RAT, width: W_DC }}
                        >
                          <span className="inline-flex items-center justify-center min-w-[1.75rem] px-1.5 py-0.5 rounded-full text-xs font-semibold bg-blue-50 dark:bg-blue-950 text-blue-700 dark:text-indigo-300">
                            {correct}
                          </span>
                        </td>

                        <td
                          className={cn(tdCls, "text-center font-bold z-10", isTop ? "bg-amber-50/40" : "bg-white dark:bg-gray-900")}
                          style={{ position: "sticky", right: W_DEL, width: W_RAT }}
                        >
                          <span className="inline-flex items-center justify-center min-w-[1.75rem] px-1.5 py-0.5 rounded-full text-xs font-semibold bg-amber-50 text-amber-700">
                            {score}
                          </span>
                        </td>

                        <td
                          className={cn(tdCls, "text-center z-10", isTop ? "bg-amber-50/40" : "bg-white dark:bg-gray-900")}
                          style={{ position: "sticky", right: 0, width: W_DEL }}
                        >
                          {canEdit && !isLocked && (
                            <button
                              onClick={() => deleteTeam(team.key)}
                              className="w-7 h-7 rounded-lg flex items-center justify-center text-gray-300 hover:text-red-400 hover:bg-red-50 dark:bg-red-950 transition-colors mx-auto"
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>

                {!isSummaryTab && (
                  <tfoot>
                    <tr className="bg-gray-50/80 border-t-2 border-gray-100 dark:border-gray-800">
                      <td className="sticky left-0 z-10 bg-gray-50/80 px-3 py-2.5 text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wide min-w-[180px]">
                        Çətinlik
                      </td>
                      {questionRatings.map((rating, i) => (
                        <td key={i} className="px-3 py-2.5 text-center w-[72px]">
                          <span className={cn(
                            "inline-flex items-center justify-center min-w-[1.5rem] px-1.5 py-0.5 rounded-full text-xs font-semibold",
                            rating >= teams.length * 0.7  && "bg-green-50 dark:bg-green-950 text-green-600",
                            rating >= teams.length * 0.3 && rating < teams.length * 0.7 && "bg-yellow-50 dark:bg-yellow-950 text-yellow-600",
                            rating <  teams.length * 0.3  && "bg-red-50 dark:bg-red-950 text-red-500",
                            teams.length === 0            && "bg-gray-50 dark:bg-gray-950 text-gray-400 dark:text-gray-500",
                          )}>
                            {rating}
                          </span>
                        </td>
                      ))}
                      <td style={{ position: "sticky", right: W_DEL + W_RAT, width: W_DC }} className="bg-gray-50/80 border-l border-gray-100 dark:border-gray-800" />
                      <td style={{ position: "sticky", right: W_DEL, width: W_RAT }}        className="bg-gray-50/80" />
                      <td style={{ position: "sticky", right: 0, width: W_DEL }}            className="bg-gray-50/80" />
                    </tr>
                  </tfoot>
                )}
              </table>
            </div>
          )}
        </div>

        {/* ── Legend ────────────────────────────────────────────────────── */}
        <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-100 dark:border-gray-800 shadow-sm px-6 py-4">
          <p className="text-xs font-bold text-gray-400 dark:text-gray-500 uppercase tracking-wide mb-2">Qaydalar</p>
          <ul className="space-y-1 text-xs text-gray-500 dark:text-gray-400">
            <li>• Xanaya klikləyərək cavabı dəyişin: <span className="font-semibold">Boş → Doğru → Yanlış → Boş</span></li>
            <li>• <span className="font-semibold">DC</span> — aktiv turda doğru cavabların sayı</li>
            <li>• <span className="font-semibold">Reytinq</span> — bütün turlardakı doğru cavabların çətinlik dərəcəsi cəmi</li>
            <li>• <span className="font-semibold">Yekun</span> tabı hər tur üzrə DC-ləri və ümumi nəticəni göstərir</li>
            <li>• Excel ixrac edib eyni faylı dolduraraq idxal etdikdə nəticələr avtomatik oyun masasına uyğunlaşdırılır</li>
            <li>• Kilidlənmiş oyunda komanda əlavə etmək/silmək mümkün deyil; cavablar dəyişdirilə bilər</li>
          </ul>
        </div>

      </div>

      {/* ── Team mapping modal ─────────────────────────────────────────────── */}
      {mappingOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
          <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-xl w-full max-w-lg max-h-[90vh] flex flex-col">
            {/* Header */}
            <div className="px-6 py-5 border-b border-gray-100 dark:border-gray-800">
              <h2 className="text-base font-bold text-gray-900 dark:text-gray-50">Komanda uyğunlaşdırması</h2>
              <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">
                Oyundakı hər komandanı verilənlər bazasındakı komanda ilə uyğunlaşdırın. Reytinq hesablanması üçün istifadə olunacaq.
              </p>
              <div className="mt-3 flex flex-wrap items-center gap-2">
                <span className="text-[11px] font-semibold px-2 py-1 rounded-full bg-green-100 text-green-700">
                  Uyğunlaşdırılıb: {mappingMeta.mappedCount}
                </span>
                <span className="text-[11px] font-semibold px-2 py-1 rounded-full bg-amber-100 text-amber-700">
                  Uyğunlaşdırılmayıb: {mappingMeta.unmappedCount}
                </span>
                <span className="text-[11px] font-semibold px-2 py-1 rounded-full bg-blue-100 text-blue-700 dark:text-indigo-300">
                  Avto uyğun: {mappingMeta.autoMappableCount}
                </span>
                {mappingMeta.mismatchCount > 0 && (
                  <span className="text-[11px] font-semibold px-2 py-1 rounded-full bg-red-100 text-red-700">
                    Səhv uyğun: {mappingMeta.mismatchCount}
                  </span>
                )}
              </div>
              <div className="mt-3 flex items-center justify-between gap-3">
                <button
                  type="button"
                  onClick={autoMapNow}
                  className="text-xs font-semibold text-blue-600 hover:text-blue-700 dark:text-indigo-300 dark:hover:text-indigo-200"
                >
                  Avtomatik uyğunlaşdır
                </button>
                <label className="inline-flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
                  <input
                    type="checkbox"
                    checked={showOnlyUnmapped}
                    onChange={e => setShowOnlyUnmapped(e.target.checked)}
                  />
                  Yalnız uyğunlaşdırılmayanlar
                </label>
              </div>
            </div>

            {/* Team rows */}
            <div className="overflow-y-auto flex-1 px-6 py-4 space-y-4">
              {visibleMappingTeams.map((team) => {
                const currentMappedId = mappings[team.key];
                const search = mappingSearch[team.key] ?? "";
                const filtered = allTeams.filter((dt: SystemTeam) =>
                  dt.name.toLowerCase().includes(search.toLowerCase())
                );
                const isMatched = currentMappedId != null;
                const meta = mappingMeta.byTeam[team.key];
                const exactMatches = meta?.exactMatches ?? [];
                const exactCandidate = exactMatches.length === 1 ? exactMatches[0] : null;
                const isMismatch = !!meta?.isMismatch;
                const canAutoMap = !!meta?.canAutoMap;

                return (
                  <div key={team.key}>
                    <div className="flex items-center justify-between mb-1.5">
                      <span className="text-sm font-semibold text-gray-800 dark:text-gray-100">{team.name}</span>
                      {isMismatch ? (
                        <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-red-100 text-red-700">Səhv uyğun</span>
                      ) : meta?.isExact ? (
                        <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-green-100 text-green-700">Dəqiq uyğun</span>
                      ) : isMatched ? (
                        <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-blue-100 text-blue-700 dark:text-indigo-300">Uyğunlaşdırıldı</span>
                      ) : canAutoMap ? (
                        <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-indigo-100 text-indigo-700">Dəqiq uyğun var</span>
                      ) : exactMatches.length > 1 ? (
                        <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-yellow-100 text-yellow-700">Bir neçə uyğun</span>
                      ) : (
                        <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300">Uyğun tapılmadı</span>
                      )}
                    </div>
                    {exactCandidate && (
                      <p className={cn(
                        "text-[11px] mb-1.5",
                        isMismatch ? "text-red-600" : "text-gray-500 dark:text-gray-400"
                      )}>
                        Dəqiq uyğun: <span className="font-semibold">{exactCandidate.name}</span>
                      </p>
                    )}
                    <div className="relative">
                      <input
                        type="text"
                        className="w-full border border-gray-200 dark:border-gray-700 rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-blue-400 pr-8"
                        placeholder="Komanda axtar..."
                        value={search}
                        onFocus={() => setMappingFocusedKey(team.key)}
                        onBlur={() => {
                          setTimeout(() => {
                            setMappingFocusedKey(prev => (prev === team.key ? null : prev));
                          }, 120);
                        }}
                        onChange={e => {
                          const val = e.target.value;
                          setMappingSearch(prev => ({ ...prev, [team.key]: val }));
                          if (!val) setMappings(prev => ({ ...prev, [team.key]: null }));
                        }}
                      />
                      {isMatched && (
                        <button
                          type="button"
                          className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-300 hover:text-red-400"
                          onClick={() => {
                            setMappings(prev => ({ ...prev, [team.key]: null }));
                            setMappingSearch(prev => ({ ...prev, [team.key]: "" }));
                            setMappingFocusedKey(null);
                          }}
                        >
                          ✕
                        </button>
                      )}
                      {mappingFocusedKey === team.key && search && filtered.length > 0 && (
                        <div className="absolute z-10 w-full mt-1 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl shadow-lg overflow-hidden max-h-40 overflow-y-auto">
                          {filtered.map((dt: SystemTeam) => (
                            <button
                              key={dt.id}
                              type="button"
                              className="w-full px-3 py-2 text-left text-sm hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
                              onMouseDown={(e) => e.preventDefault()}
                              onClick={() => {
                                setMappings(prev => ({ ...prev, [team.key]: dt.id }));
                                setMappingSearch(prev => ({ ...prev, [team.key]: dt.name }));
                                setMappingFocusedKey(null);
                              }}
                            >
                              {dt.name}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
              {visibleMappingTeams.length === 0 && (
                <div className="text-center text-sm text-gray-400 dark:text-gray-500 py-6">
                  Uyğunlaşdırılmayan komanda qalmayıb.
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="px-6 py-4 border-t border-gray-100 dark:border-gray-800 flex items-center justify-between gap-3">
              <div className="text-xs text-gray-400 dark:text-gray-500">
                {mappingMeta.mappedCount} / {teams.length} uyğunlaşdırıldı
              </div>
              <div className="flex gap-2">
                <button
                  type="button"
                  className="px-4 py-2 text-sm font-semibold text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 transition-colors"
                  onClick={() => setMappingOpen(false)}
                >
                  Ləğv et
                </button>
                <button
                  type="button"
                  disabled={completeLoading || mappingMeta.mismatchCount > 0}
                  onClick={confirmAndComplete}
                  className="flex items-center gap-1.5 px-4 py-2 rounded-xl bg-purple-600 text-white text-sm font-semibold hover:bg-purple-700 disabled:opacity-50 transition-colors"
                >
                  {completeLoading && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
                  Tamamla
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LiveGame;
